import { SETUP_WIZARD_SHARE_LABELS, SETUP_WIZARD_STEP_NAMES, setupWizardStepLabel } from './setup-wizard';

const ONBOARDING_STEP_LABELS: Record<string, string> = {
  wizard: 'Wizard',
  plans: 'Plans',
  checkout: 'Checkout',
  book_call: 'Book a Call',
  completed: 'Completed',
};

/** The quiet account email a quiet_email_sent event was about (its metadata.kind). */
const QUIET_EMAIL_LABELS: Record<string, string> = {
  traffic_stopped: 'Visitors stopped',
  no_finish: 'Started, not finished',
};

const stepLabel = (step: unknown) => ONBOARDING_STEP_LABELS[String(step)] ?? String(step);
const dollars = (cents: unknown) => `$${(Number(cents) / 100).toFixed(2)}`;

/**
 * What an onboarding step change, past-due form pause, template import, X-Ray funds grant or a
 * customer leaving the setup wizard, or a quiet account email shows in the activity logs. Other event types get nothing here. Empty values are left out.
 */
export function platformEventBadges(eventType: string, md: Record<string, unknown>): Array<{ label: string; value: string }> {
  const badges: Array<{ label: string; value: string }> = [];
  switch (eventType) {
    case 'onboarding_step_changed':
      if (md.to) badges.push({ label: 'Step', value: md.from ? `${stepLabel(md.from)} → ${stepLabel(md.to)}` : stepLabel(md.to) });
      break;
    case 'forms_paused_past_due':
      if (md.draftedCount != null) badges.push({ label: 'Forms', value: String(md.draftedCount) });
      if (md.gracePeriodDays != null) badges.push({ label: 'Grace Period', value: `${md.gracePeriodDays} days` });
      break;
    case 'form_imported':
      if (md.source === 'form_template') badges.push({ label: 'Source', value: 'Template' });
      break;
    case 'setup_wizard_left':
      // Only the three steps there are: a row from the six-step wizard (test accounts only) shows none.
      if (typeof md.step === 'number' && md.step in SETUP_WIZARD_STEP_NAMES) {
        badges.push({ label: 'Step', value: setupWizardStepLabel(md.step) });
      }
      break;
    case 'setup_wizard_published': {
      const share = SETUP_WIZARD_SHARE_LABELS[String(md.shareChoice)];
      if (share) badges.push({ label: 'Shared as', value: share });
      break;
    }
    case 'quiet_email_sent': {
      const kind = QUIET_EMAIL_LABELS[String(md.kind)];
      if (kind) badges.push({ label: 'Email', value: kind });
      break;
    }
    case 'xray_funds_granted':
      if (md.amountCents != null) badges.push({ label: 'Amount', value: dollars(md.amountCents) });
      if (md.balanceCents != null) badges.push({ label: 'Balance', value: dollars(md.balanceCents) });
      break;
  }
  return badges;
}

const LOGIN_METHOD_LABELS: Record<string, string> = {
  password: 'Password',
  email: 'Email',
  google: 'Google',
  slack: 'Slack',
  magic_link: 'Login link',
};

/** How someone logged in or signed up (metadata.method). An unknown method shows as stored. */
export const loginMethodLabel = (method: unknown): string | null =>
  method ? LOGIN_METHOD_LABELS[String(method)] ?? String(method) : null;

const PROVIDER_LABELS: Record<string, string> = {
  go_high_level: 'GoHighLevel',
  ghl: 'GoHighLevel',
  calendly: 'Calendly',
  google: 'Google Calendar',
  webhook: 'Webhook',
};

/** An integration's provider (metadata.provider). An unknown provider shows as stored. */
export const providerLabel = (provider: unknown): string | null =>
  provider ? PROVIDER_LABELS[String(provider)] ?? String(provider) : null;

/** Where a billing change came from (metadata.source). Other sources (signup, imports) are not shown here. */
const CHANGE_SOURCE_LABELS: Record<string, string> = { app: 'App', stripe: 'Stripe' };

const DIRECTION_LABELS: Record<string, string> = { upgrade: 'Upgrade', downgrade: 'Downgrade' };

const TEAM_CONTEXT_LABELS: Record<string, string> = { agency: 'Agency', subaccount: 'Subaccount' };

/** Where a customer left the cancel flow (cancel_flow_left metadata.lastStep). */
const CANCEL_FLOW_STEP_LABELS: Record<string, string> = { reason: 'Reason step', offer: 'Offer step' };

/** An integration problem's kind (back-end-next/src/lib/integration-health/kinds.ts PROBLEM_KINDS). */
const PROBLEM_LABELS: Record<string, string> = {
  ghl_disconnected: 'GoHighLevel disconnected',
  ghl_location_inactive: 'GoHighLevel location inactive',
  calendly_disconnected: 'Calendly disconnected',
  calendar_deleted: 'Calendar deleted',
  calendar_deactivated: 'Calendar turned off',
  calendar_not_connected: 'Calendar not connected',
  webhook_failing: 'Webhook failing',
};

/** Events whose reason shows here. Extra subaccount events show theirs through extraSubaccountBadges. */
const REASON_EVENTS = new Set(['cancelled', 'cancel_flow_left']);

const FIRST_EVENTS = new Set(['first_submission_received', 'first_lead_captured', 'first_appointment_booked']);

const DAY_MS = 24 * 60 * 60 * 1000;

/** 'some_code' reads "Some code". */
const humanize = (value: unknown) => {
  const text = String(value).replace(/_/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const finite = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** $297, or $297.50 when there are cents. */
const usd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

const ordinal = (n: number) => {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n.toLocaleString('en-US')}${suffix}`;
};

/** Agency owners are stored as super_admin. */
const roleLabel = (role: unknown, context: unknown) =>
  context === 'agency' && role === 'super_admin' ? 'Owner' : humanize(role);

/**
 * What the day-to-day events show in the activity logs, read from their metadata keys: logins, billing changes,
 * payments, form and team changes, leads, webhooks and integration problems. Also the reason of a cancellation, the
 * invoice of a failed payment, and "After signup" on an agency's first submission, lead and appointment when its
 * signup date is given. Only keys that are present show.
 */
export function activityBadges(
  event: { eventType: string; metadata: Record<string, any> | null; createdAtUtc: string },
  opts: { agencyCreatedAtUtc?: string; timezone?: string } = {},
): Array<{ label: string; value: string }> {
  const md: Record<string, any> = event.metadata || {};
  const badges: Array<{ label: string; value: string }> = [];
  const add = (label: string, value: string | null | undefined) => {
    if (value) badges.push({ label, value });
  };
  const count = (label: string, value: unknown) => {
    const n = finite(value);
    if (n !== null) add(label, n.toLocaleString('en-US'));
  };
  const money = (label: string, value: unknown) => {
    const n = finite(value);
    if (n !== null) add(label, usd(n));
  };

  if (REASON_EVENTS.has(event.eventType) && md.reason) add('Reason', String(md.reason));
  add('Method', loginMethodLabel(md.method));
  if (md.fromPlanName && md.toPlanName) add('Plan', `${md.fromPlanName} to ${md.toPlanName}`);
  else if (md.toPlanName) add('Plan', String(md.toPlanName));
  add('Change', DIRECTION_LABELS[String(md.direction)]);
  const before = finite(md.monthlyBefore);
  const after = finite(md.monthlyAfter);
  if (before !== null && after !== null) add('Monthly', `${usd(before)} to ${usd(after)}`);
  money('MRR lost', md.mrrLost);
  if (md.endsAt) {
    const endsAt = new Date(md.endsAt);
    // An empty saved timezone means the browser's own, as in RelativeTime.
    if (!isNaN(endsAt.getTime())) {
      add('Ends', endsAt.toLocaleDateString('en-US', { timeZone: opts.timezone || undefined, month: 'short', day: 'numeric', year: 'numeric' }));
    }
  }
  add('Source', CHANGE_SOURCE_LABELS[String(md.source)]);
  // trial_converted kept only amountPaid (cents) before it carried amount (dollars).
  if (md.amount != null) money('Amount', md.amount);
  else if (event.eventType === 'trial_converted' && finite(md.amountPaid) !== null) money('Amount', Number(md.amountPaid) / 100);
  count('Attempt', md.attempt);
  if (md.declineReason) add('Decline reason', humanize(md.declineReason));
  if (event.eventType === 'payment_failed' && md.invoiceId) add('Invoice', String(md.invoiceId));
  const trialDays = finite(md.trialDays);
  if (trialDays !== null) add('Trial', `${trialDays} ${trialDays === 1 ? 'day' : 'days'}`);
  if (md.firstPublish === true) add('First publish', 'Yes');
  count('Questions', md.questionCount);
  count('Leads', md.leadCount);
  if (typeof md.wasPublished === 'boolean') add('Was live', md.wasPublished ? 'Yes' : 'No');
  if (md.sourceFormName) add('Copied from', String(md.sourceFormName));
  if (md.lastStep) add('Left at', CANCEL_FLOW_STEP_LABELS[String(md.lastStep)] ?? humanize(md.lastStep));
  count('Rows', md.rows);
  if (md.page) add('Page', String(md.page));
  count('Count', md.count);
  if (md.host) add('Webhook', String(md.host));
  if (md.problem) add('Problem', PROBLEM_LABELS[String(md.problem)] ?? humanize(md.problem));
  if (md.targetEmail) add('Email', String(md.targetEmail));
  if (md.role) add('Role', roleLabel(md.role, md.context));
  add('Access', TEAM_CONTEXT_LABELS[String(md.context)]);
  if (md.templateName || md.templateKey) add('Template', String(md.templateName || md.templateKey));
  const milestone = finite(md.milestone);
  if (milestone !== null) add('Milestone', `${ordinal(milestone)} lead`);
  count('Forms', md.formCount);
  if (Array.isArray(md.formNames) && md.formNames.length > 0) add('Form names', md.formNames.join(', '));

  if (FIRST_EVENTS.has(event.eventType) && opts.agencyCreatedAtUtc) {
    const days = Math.floor((new Date(event.createdAtUtc).getTime() - new Date(opts.agencyCreatedAtUtc).getTime()) / DAY_MS);
    if (days >= 0) add('After signup', `${days} ${days === 1 ? 'day' : 'days'}`);
  }
  return badges;
}
